import React, { createContext, useState, useContext, ReactNode, useEffect, useRef, useCallback } from 'react';
import { apiService, Project } from '../services/apiService';
import { useAuth } from './AuthContext';
import { parseApiDateTime } from '../utils/dateTimeUtils';

const STORAGE_KEY_COLLECTIONS_NAV = 'collectionsNavState';

const sortProjectsByCreatedAt = (list: Project[]): Project[] =>
  [...list].sort((a, b) => {
    const dateA = parseApiDateTime(a.createdAt)?.getTime() ?? 0;
    const dateB = parseApiDateTime(b.createdAt)?.getTime() ?? 0;
    if (dateA !== dateB) return dateA - dateB;
    return a.id - b.id;
  });

// Define the shape of the context state
interface ProjectContextType {
  projects: Project[];
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  isLoading: boolean;
  error: string | null;
  refreshProjects: () => void;
}

// Create the context
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Create the provider component
export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  /** Latest selection for async fetch completion — avoids stale closure overwriting user pick. */
  const selectedProjectRef = useRef<Project | null>(null);
  selectedProjectRef.current = selectedProject;
  const projectsRef = useRef<Project[]>([]);
  projectsRef.current = projects;

  const { isAuthenticated, user, token, isLoading: authLoading } = useAuth();

  const fetchProjects = useCallback(async (silent = false) => {
    if (!silent) {
      console.log('=== PROJECT CONTEXT DEBUG ===');
      console.log('isAuthenticated:', isAuthenticated);
      console.log('Starting to fetch projects...');
      console.log('JWT token present:', !!localStorage.getItem('token'));

      setIsLoading(true);
      setError(null);
    }
    try {
      const fetchedProjects = sortProjectsByCreatedAt(await apiService.getProjects());
      if (!silent) {
        console.log('Projects fetched successfully:', fetchedProjects);
      }
      const previous = projectsRef.current;
      const unchanged =
        previous.length === fetchedProjects.length &&
        previous.every((project, index) => {
          const next = fetchedProjects[index];
          return project.id === next.id && project.name === next.name && project.description === next.description;
        });
      if (unchanged) {
        return;
      }
      setProjects(fetchedProjects);

      // Ensure selected project is always valid for the current logged-in user.
      // On account switch, previous user's selected project can become stale and make
      // projects/collections appear missing until a manual selection is made.
      const currentSelected = selectedProjectRef.current;
      const currentStillValid =
        currentSelected != null && fetchedProjects.some((p) => p.id === currentSelected.id);

      if (fetchedProjects.length === 0) {
        setSelectedProject(null);
      } else if (currentStillValid) {
        // Keep the existing selection reference when the id is unchanged.
        // Replacing it on every refresh remounts Collections (effect deps on selectedProject).
      } else {
        // Restore last-used project from Collections navigation state when available; fall back to first project.
        let projectToSelect: Project | null = null;
        try {
          const stored = localStorage.getItem(STORAGE_KEY_COLLECTIONS_NAV);
          if (stored) {
            const { projectId } = JSON.parse(stored);
            if (projectId != null) {
              const match = fetchedProjects.find((p) => p.id === Number(projectId));
              if (match) {
                projectToSelect = match;
              }
            }
          }
        } catch {
          // Ignore JSON/LocalStorage errors and fall back to default behavior
        }
        if (!projectToSelect) {
          projectToSelect = fetchedProjects[0];
        }
        console.log('Auto-selecting project:', projectToSelect);
        setSelectedProject(projectToSelect);
      }
    } catch (err: any) {
      if (silent) {
        return;
      }
      console.error('Error fetching projects:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config,
        code: err.code
      });
      
      // Provide more specific error messages
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Authentication required. Please log in again.');
      } else if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
        setError('Network error: Could not connect to the server. Please check if the backend is running on http://localhost:8080');
      } else if (err.response?.status) {
        setError(`Failed to fetch projects: ${err.response?.data?.message || err.message} (Status: ${err.response?.status})`);
      } else {
        setError(`Failed to fetch projects: ${err.message || 'Unknown error'}`);
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    console.log('=== PROJECT CONTEXT useEffect ===');
    console.log('isAuthenticated:', isAuthenticated, 'authLoading:', authLoading, 'hasToken:', !!token);

    // Wait until AuthProvider has finished reading localStorage / applying login state.
    // Otherwise getProjects can fire before the JWT is available; the interceptor sends no
    // Authorization header, the API returns empty/forbidden, and the UI stays empty until a hard refresh.
    if (authLoading) {
      return;
    }

    if (isAuthenticated && token) {
      console.log('User is authenticated, fetching projects...');
      fetchProjects();
    } else if (!isAuthenticated) {
      console.log('User is not authenticated, clearing projects...');
      // Clear projects when user is not authenticated
      setProjects([]);
      setSelectedProject(null);
      setIsLoading(false);
      setError(null);
    }
  }, [isAuthenticated, user?.id, token, authLoading, fetchProjects]);

  // Pick up projects assigned to this member while the account stays open (no full page reload).
  useEffect(() => {
    if (authLoading || !isAuthenticated || !token) {
      return;
    }
    const refreshAssignedProjects = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      void fetchProjects(true);
    };
    document.addEventListener('visibilitychange', refreshAssignedProjects);
    window.addEventListener('focus', refreshAssignedProjects);
    const timer = window.setInterval(refreshAssignedProjects, 5000);
    return () => {
      document.removeEventListener('visibilitychange', refreshAssignedProjects);
      window.removeEventListener('focus', refreshAssignedProjects);
      window.clearInterval(timer);
    };
  }, [authLoading, isAuthenticated, token, fetchProjects]);

  const handleSetSelectedProject = (project: Project | null) => {
    console.log('Setting selected project:', project);

    // Persist the selected project so Collections page can restore project/collection/request on refresh
    try {
      if (project) {
        const existingRaw = localStorage.getItem(STORAGE_KEY_COLLECTIONS_NAV);
        if (existingRaw) {
          const existing = JSON.parse(existingRaw) as { projectId?: number | null; collectionId?: number | null; requestId?: number | null };
          localStorage.setItem(
            STORAGE_KEY_COLLECTIONS_NAV,
            JSON.stringify({
              ...existing,
              projectId: project.id,
            }),
          );
        } else {
          localStorage.setItem(
            STORAGE_KEY_COLLECTIONS_NAV,
            JSON.stringify({
              projectId: project.id,
              collectionId: null,
              requestId: null,
            }),
          );
        }
      }
    } catch {
      // ignore persistence errors
    }

    setSelectedProject(project);
  };

  const value = {
    projects,
    selectedProject,
    setSelectedProject: handleSetSelectedProject,
    isLoading,
    error,
    refreshProjects: fetchProjects // Expose a function to manually refresh
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

// Create a custom hook for using the context
export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
}; 